/**
 * @module components/modal
 */
import { Component } from '@roskvartal-landings/base';
import { DOMUtils } from '@roskvartal-landings/util-dom-utils';
import { Animator } from '@roskvartal-landings/module-animator';

const OPEN_EVT = 'modal-open';
const CLOSE_EVT = 'modal-close';

/**
 * @description Событие, вызывающееся при открытии модального окна
 * @event modal-open
 */
const openEvent = new CustomEvent(OPEN_EVT);
/**
 * @description Событие, вызывающееся при закрытии модального окна
 * @event modal-close
 */
const closeEvent = new CustomEvent(CLOSE_EVT);

/**
 * @extends module:common/classes/component~Component
 * @inheritdoc
 * @classdesc Компонент модального окна
 */
class Modal extends Component {
  /**
   * @param {HTMLElement} el - Корневой элемент компоненты
   */
  constructor(el) {
    super(el);
    /**
     * @member {HTMLElement}
     * @description Подложка под модалку
     */
    this.backdrop = this.refs.get('backdrop');
    /**
     * @member {HTMLElement}
     * @description Диалог
     */
    this.dialog = this.refs.get('dialog');
    Animator.animate(this.backdrop, 'u-fade-half');
    Animator.animate(this.dialog, 'u-modal');
    this.addEventListeners();
  }

  /**
   * @method
   * @description Добавиляет слушатели событий открытия/закрытия
   */
  addEventListeners() {
    this.backdrop.addEventListener('click', () => {
      this.close();
    });
    this.el.addEventListener('modal-open', (event) => {
      this.open(event.detail);
    });
    this.el.addEventListener('modal-close', (event) => {
      this.close(event.detail);
    });
    this.refs.get('close-btn').addEventListener('click', () => {
      this.close();
    });
  }

  /**
   * @method
   * @param {Object} payload - Объект настроек
   * @description Открыть модальное окно
   */
  open(payload) {
    this.payload = payload;
    document.body.classList.add('u-overflow-hidden');
    document.body.style.paddingRight = `${DOMUtils.scrollbarWidth}px`;
    [...document.querySelectorAll('[data-modal-padded]')].forEach(item => {
      item.style.paddingRight = `${parseInt(DOMUtils.getStylePropertyValue(item, 'padding-right')) + DOMUtils.scrollbarWidth}px`;
    });
    this.el.classList.add('is-opened');
    Animator.enter(this.dialog);
    Animator.enter(this.backdrop);
  }

  /**
   * @method
   * @param {Object} payload - Объект настроек
   * @description Закрыть модальное окно
   */
  close(payload) {
    this.payload = payload;
    document.body.classList.remove('u-overflow-hidden');
    document.body.style.paddingRight = '0px';
    [...document.querySelectorAll('[data-modal-padded]')].forEach(item => {
      item.style.paddingRight = `${parseInt(DOMUtils.getStylePropertyValue(item, 'padding-right')) - DOMUtils.scrollbarWidth}px`;
    });
    Animator.leave(this.dialog);
    Animator.leave(this.backdrop);
    const removeClass = () => {
      this.el.classList.remove('is-opened');
      this.el.removeEventListener('transitionend', removeClass);
    };
    this.el.addEventListener('transitionend', removeClass);
  }

  /**
   * @member {CustomEvent}
   * @static
   * @method
   * @description Событие открытия модального окна с передачей настроек
   */
  static getCustomOpenEvent(payload) {
    return new CustomEvent(OPEN_EVT, { detail: payload });
  }

  /**
   * @member {CustomEvent}
   * @static
   * @method
   * @description Событие закрытия модального окна с передачей настроек
   */
  static getCustomCloseEvent(payload) {
    return new CustomEvent(CLOSE_EVT, { detail: payload });
  }

  /**
   * @member {CustomEvent}
   * @readonly
   * @description Событие открытия модального окна
   */
  static get openEvent() {
    return openEvent;
  }

  /**
   * @member {CustomEvent}
   * @readonly
   * @description Событие закрытия модального окна
   */
  static get closeEvent() {
    return closeEvent;
  }
}

export default Modal;
